import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Fact } from '@/types';

interface UseSetKeyForFactsParams {
  key: boolean;
  factIds: string[];
}

export const useSetKeyForFacts = (caseId: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ key, factIds }: UseSetKeyForFactsParams) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.PATCH,
        data: { key, factIds },
      });

      trackCustomEvent(TrackingEvents.SetKeyForFacts);

      const response = await fetch(`${APIBaseChronos}/api/case/${caseId}/fact/set-key`, fetchConfig);

      if (!response.ok) {
        throw new Error('Failed to update key facts');
      }

      return response;
    },
    {
      onMutate: async ({ key, factIds }: UseSetKeyForFactsParams) => {
        // Cancel any outgoing refetches to avoid race conditions
        await queryClient.cancelQueries({ queryKey: [QueryEntity.KeyFacts, { caseId }] });

        // Snapshot the previous value
        const previousKeyFacts = queryClient.getQueryData<Fact[]>([QueryEntity.KeyFacts, { caseId }]);

        // Optimistically update the key facts
        queryClient.setQueryData<Fact[]>([QueryEntity.KeyFacts, { caseId }], (old) => {
          if (!old) return [];

          if (key) {
            // Adding facts as key facts - need to fetch the full fact data from CaseFacts cache
            const caseFactsCache = queryClient.getQueriesData<{ facts: Fact[] }>([QueryEntity.CaseFacts, { caseId }]);
            const allFacts: Fact[] = [];

            // Collect facts from all CaseFacts cache entries
            caseFactsCache.forEach(([, data]) => {
              if (data?.facts) {
                allFacts.push(...data.facts);
              }
            });

            // Find the facts to add
            const factsToAdd = allFacts.filter(
              (fact) => factIds.includes(fact.id) && !old.some((kf) => kf.id === fact.id),
            );

            return [...old, ...factsToAdd];
          } else {
            // Removing facts from key facts
            return old.filter((fact) => !factIds.includes(fact.id));
          }
        });

        // Return context with the snapshot for rollback
        return { previousKeyFacts };
      },
      onSuccess: (_, { key }) => {
        // Invalidate to ensure data consistency (will refetch in background)
        queryClient.invalidateQueries({ queryKey: [QueryEntity.KeyFacts, { caseId }] });

        const action = key ? 'added to' : 'removed from';
        toast.success(`Successfully ${action} key facts`);
      },
      onError: (error, _, context) => {
        // Rollback on error
        if (context?.previousKeyFacts) {
          queryClient.setQueryData([QueryEntity.KeyFacts, { caseId }], context.previousKeyFacts);
        }

        console.error('Set key for facts error:', error);
        toast.error('Failed to update key facts');
      },
    },
  );
};
